import { subjectPhotoGradient } from "Components/WebsiteTheme/subjectPhoto.style";
import styled from "styled-components";
import { C, MQ } from "Components/WebsiteTheme/WebsiteTheme.style";

/* ── Hero ──────────────────────────────────────────────────────────── */

export const AboutHero = styled.section`
	.sm_hero_photo img {
		object-position: center 38%;
	}

	.ah_subline {
		max-width: 520px;
	}
`;

/* ── Mission and What We Believe ───────────────────────────────────── */

export const AboutMissionSection = styled.section`
	/* First section under the hero: a little closer than a normal section (beats .sm_section's padding) */
	&&& {
		padding-top: 56px;

		${MQ.PHONE} {
			padding-top: 40px;
		}
	}

	.am_mission {
		display: flex;
		align-items: center;
		gap: 28px;
		padding: 32px 40px;
		border: 1px solid ${C.greenLine};
		border-radius: 20px;
		background: ${C.greenSoft};

		.sm_icon {
			width: 72px;
			height: 72px;

			svg {
				width: 36px;
				height: 36px;
			}
		}

		${MQ.PHONE} {
			flex-direction: column;
			align-items: flex-start;
			gap: 16px;
			padding: 24px 20px;

			.sm_icon {
				width: 56px;
				height: 56px;

				svg {
					width: 28px;
					height: 28px;
				}
			}
		}
	}

	.am_mission_copy {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.am_beliefs {
		display: grid;
		grid-template-columns: 220px repeat(3, minmax(0, 1fr));
		align-items: stretch;
		gap: 20px;
		margin-top: 56px;

		${MQ.STACK} {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}

		${MQ.TABLET} {
			grid-template-columns: 1fr;
			gap: 16px;
			margin-top: 48px;
		}
	}

	.am_beliefs_head {
		align-self: center;

		/* Short accent rule under the title */
		.sm_title::after {
			content: "";
			display: block;
			width: 56px;
			height: 4px;
			margin-top: 14px;
			border-radius: 4px;
			background: ${C.blue};
		}

		${MQ.STACK} {
			grid-column: 1 / -1;
		}
	}

	.am_belief {
		display: flex;
		align-items: flex-start;
		gap: 16px;
		height: 100%;
		padding: 24px;
		border-width: 1px;
		border-style: solid;
		border-radius: 16px;

		.sm_icon {
			background: #fff;
		}
	}

	.am_belief_copy {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}
`;

/* ── Subjects ──────────────────────────────────────────────────────── */

export const AboutSubjectsSection = styled.section`
	.as_head {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		align-items: end;
		gap: 24px 64px;

		${MQ.TABLET} {
			grid-template-columns: 1fr;
		}
	}

	.as_grid {
		display: grid;
		grid-template-columns: repeat(7, minmax(0, 1fr));
		gap: 14px;
		margin-top: 40px;

		${MQ.STACK} {
			grid-template-columns: repeat(4, minmax(0, 1fr));
		}

		${MQ.PHONE} {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 12px;
		}
	}

	/* Seventh tile spans two columns on the 4- and 2-column layouts, as on Home */
	.as_cell--wide {
		${MQ.STACK} {
			grid-column: span 2;
		}
	}

	.as_tile {
		position: relative;
		display: block;
		width: 100%;
		height: 100%;
		aspect-ratio: 10 / 11;
		overflow: hidden;
		padding: 0;
		border: 0;
		border-radius: 12px;
		background: ${C.navy};
		font-family: inherit;
		cursor: pointer;
		transition: transform 0.35s ${C.ease}, box-shadow 0.35s ${C.ease};

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
			transition: transform 0.6s ${C.ease};
		}

		${subjectPhotoGradient}

		&:hover {
			transform: translateY(-4px);
			border-color: ${C.blueLine};
			box-shadow: ${C.cardShadowHover};

			img {
				transform: scale(1.08);
			}
		}

		&:focus-visible {
			outline: 3px solid ${C.blue};
			outline-offset: 3px;
		}
	}

	.as_cell--wide .as_tile {
		${MQ.STACK} {
			aspect-ratio: auto;
			height: 100%;
		}

		${MQ.PHONE} {
			aspect-ratio: 2 / 1;
			height: auto;
		}

		img {
			${MQ.STACK} {
				position: absolute;
				inset: 0;
			}
		}
	}

	.as_label {
		position: absolute;
		right: 10px;
		bottom: 12px;
		left: 12px;
		z-index: 1;
		color: #fff;
		font-size: 16px;
		font-weight: 700;
		line-height: 1.2;
		text-align: left;

		${MQ.PHONE} {
			font-size: 15px;
		}
	}
`;

/* ── Founder ───────────────────────────────────────────────────────── */

export const AboutFounderSection = styled.section`
	.af_card {
		display: grid;
		grid-template-columns: 240px minmax(0, 1fr) 260px;
		align-items: center;
		gap: 40px;
		padding: 32px 40px;
		border: 1px solid ${C.creamLine};
		border-radius: 20px;
		background: ${C.cream};

		${MQ.STACK} {
			grid-template-columns: 220px minmax(0, 1fr);
		}

		${MQ.SMALL} {
			grid-template-columns: 1fr;
			gap: 24px;
			padding: 24px 20px;
		}
	}

	.af_photo {
		width: 100%;
		height: auto;
		aspect-ratio: 1 / 1;
		border-radius: 16px;
		object-fit: cover;
		object-position: center 30%;
		box-shadow: ${C.cardShadow};

		${MQ.SMALL} {
			max-width: 240px;
		}
	}

	.af_copy {
		display: flex;
		flex-direction: column;
		gap: 10px;

		${MQ.SMALL} {
			order: -2;
		}
	}

	.af_role {
		color: ${C.body};
		font-size: 16px;
		font-weight: 500;
	}

	.af_quote {
		color: ${C.navy};
		text-align: center;
		transform: rotate(-4deg);

		${MQ.STACK} {
			grid-column: 2;
			text-align: left;
			transform: none;
		}

		${MQ.SMALL} {
			grid-column: 1;
			order: -1;
		}
	}
`;
